import { ErrorModal, HelmetDexteel, useToken } from "@dexteel/mesf-core";
import {
	Button,
	Card,
	CardContent,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	type DialogProps,
	DialogTitle,
	Grid,
	Snackbar,
	TextField,
	Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Alert from "@material-ui/lab/Alert";
import moment from "moment";
import { type SyntheticEvent, useCallback, useEffect, useState } from "react";
import { useWorkOrdersContext } from "../../context/workOrdersContext";
import { closeWorkOrders } from "../../repositorie/WorkOrdersRepositorie";

const useStyles = makeStyles((theme) => ({
	root: {
		"& .input-group-text": {
			display: "none",
		},
		"& .MuiPaper-root": {
			height: "auto",
		},
	},
	modalTitle: {
		backgroundColor: "#ced2cc",
		color: "#495057",
		textAlign: "center",
	},
	modalTitleCloseWO: {
		backgroundColor: "#28a745",
		color: "white",
		textAlign: "center",
	},
	modalWarning: {
		color: "white",
		backgroundColor: "#dc3545",
		textAlign: "center",
	},
	messageTitle: {
		fontSize: 15,
		fontWeight: "bold",
		color: "#0000008A",
	},
	responsive: {
		[theme.breakpoints.up("md")]: {
			padding: "0 10px 0 0 !important",
			marginTop: "0 !important",
		},
	},
	responsiveTwo: {
		[theme.breakpoints.up("md")]: {
			padding: "0 0 0 10px !important",
			marginTop: "0 !important",
		},
	},
	responsiveThree: {
		[theme.breakpoints.up("md")]: {
			padding: "0 !important",
		},
	},
	responsiveFour: {
		[theme.breakpoints.up("md")]: {
			padding: "0 10px 0 0 !important",
		},
	},
	responsiveFive: {
		[theme.breakpoints.up("md")]: {
			padding: "0 0 0 10px !important",
		},
	},
}));

type Props = {
	show: boolean;
	onHide: (onHide: boolean) => void;
	suffixTitle?: string;
};

export const CloseGroupWorkOrdersDialog = ({
	onHide,
	show,
	suffixTitle,
}: Props) => {
	const [closeSuccess, setCloseSuccess] = useState(false);
	const [fullWidth, setFullWidth] = useState(true);
	const [maxWidth, setMaxWidth] = useState<DialogProps["maxWidth"]>("md");
	const classes = useStyles();
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState<string>("");
	const [userInput, setUserInput] = useState<string>("");
	const { getUserName } = useToken();
	const {
		state: {
			dataToGetWorkOrders: { AssetId },
			listWorkOrderSelected,
			commentCloseGroupWO,
			dataToCloseWOGroup,
		},
		actions: { setCommentCloseGroupWO, setDataToCloseWOGroup },
	} = useWorkOrdersContext();
	const listWorkOrderSelectedCount = listWorkOrderSelected.length;

	const handleClose = (event?: SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		onHide(false);
	};

	const handleCancel = () => {
		onHide(false);
	};

	const appendComment = (newComment: string) => {
		const now = moment().format("DD/MM/YYYY HH:mm");
		let newValue = "";
		if (
			commentCloseGroupWO === "" ||
			commentCloseGroupWO === null ||
			commentCloseGroupWO === "null"
		) {
			newValue = getUserName() + " - " + now + " - " + newComment;
		} else {
			newValue =
				commentCloseGroupWO +
				"\n" +
				getUserName() +
				" - " +
				now +
				" - " +
				newComment;
		}
		if (newComment != "") {
			setUserInput("");
			setCommentCloseGroupWO(newValue);
		}
	};

	const addNewComment = () => {
		appendComment(userInput);
	};

	const onKeyDown = (e: any) => {
		if (e.key === "Enter") {
			appendComment(e.target.value);
			e.preventDefault();
		}
	};

	const changeComments = (value: string) => {
		setCommentCloseGroupWO(value);
	};

	const closeSelectedWorkOrders = useCallback(() => {
		if (dataToCloseWOGroup) {
			setIsSubmitLoading(true);
			const errors: string[] = [];

			const promises = dataToCloseWOGroup.map((workOrder) =>
				closeWorkOrders(workOrder.data?.id, workOrder.Comments).then((res) => {
					if (!res.ok) {
						errors.push(
							`Error closing work order ${workOrder.data?.Title} : ${res.message}.`,
						);
					}
					return res.ok;
				}),
			);

			Promise.all(promises)
				.then((results) => {
					if (results.every((result) => result === true)) {
						setTimeout(() => {
							setCloseSuccess(true);
							onHide(true);
							setIsSubmitLoading(false);
						}, 100);
					} else {
						setIsSubmitLoading(false);
						if (errors.length > 0) {
							const formattedError = errors.join("  ");
							setError(formattedError);
						}
					}
				})
				.catch((error) => {
					setIsSubmitLoading(false);
					setError(error.message);
				});
		}
	}, [dataToCloseWOGroup, listWorkOrderSelected, AssetId]);

	useEffect(() => {
		for (let i = 0; i < listWorkOrderSelected.length; i++) {
			listWorkOrderSelected[i].Comments = `${
				listWorkOrderSelected[i].data?.Comments
					? listWorkOrderSelected[i].data?.Comments + "\n" + commentCloseGroupWO
					: "\n" + commentCloseGroupWO
			} `;
		}
		setDataToCloseWOGroup(listWorkOrderSelected);
	}, [listWorkOrderSelected, commentCloseGroupWO, show]);

	useEffect(() => {
		if (show) {
			setCommentCloseGroupWO("");
			setUserInput("");
			setIsSubmitLoading(false);
		}
	}, [show]);

	return (
		<>
			{show && (
				<HelmetDexteel
					title={`Close Group Work Orders${suffixTitle ? ` - ${suffixTitle}` : ""}`}
				/>
			)}
			<Dialog
				fullWidth={fullWidth}
				fullScreen={true}
				maxWidth={maxWidth}
				open={show}
				onClose={() => handleCancel()}
				style={{ margin: "20px" }}
				className={classes.root}
			>
				<Grid container style={{ padding: "15px 0 0" }}>
					<Grid item md={12} xs={12}>
						<DialogTitle
							id="max-width-dialog-title"
							style={{ padding: "5px 24px 10px" }}
						>
							{" "}
							CLOSE: {listWorkOrderSelectedCount} WORK ORDERS
						</DialogTitle>
					</Grid>
					<Grid container item>
						<DialogContent
							dividers
							style={{ height: "70vh", paddingTop: "10px" }}
						>
							<Grid container style={{ height: "100%%" }}>
								<Grid
									item
									md={12}
									xs={12}
									style={{
										height: "100%",
										marginBottom: "15px",
										marginTop: "15px",
										padding: 0,
									}}
								>
									<Card
										variant="outlined"
										style={{ backgroundColor: "#E9F2F8", height: "100%" }}
										className={classes.root}
									>
										<CardContent style={{ height: "55.5vh" }}>
											<Typography className={classes.messageTitle}>
												Comments
											</Typography>
											<textarea
												value={commentCloseGroupWO as string}
												onChange={changeComments as any}
												disabled
												style={{
													resize: "none",
													width: "100%",
													border: "none",
													backgroundColor: "#E9F2F8",
													fontSize: 14,
												}}
											></textarea>
										</CardContent>
									</Card>
								</Grid>
								<Grid
									container
									spacing={2}
									justifyContent="center"
									alignItems="center"
								>
									<Grid item md={9} xs={12} style={{ padding: "0 8px" }}>
										<TextField
											variant="outlined"
											margin="dense"
											multiline
											maxRows={3}
											value={userInput}
											onChange={(e) => setUserInput(e.target.value)}
											label="Comment"
											fullWidth
											onKeyDown={onKeyDown}
											autoComplete="off"
										/>
									</Grid>
									<Grid item md={3} xs={12}>
										<Button
											fullWidth
											variant="contained"
											color="primary"
											onClick={addNewComment}
											style={{ height: "40px", marginTop: 2, padding: "0 8px" }}
										>
											Add comment
										</Button>
									</Grid>
								</Grid>
							</Grid>
						</DialogContent>
					</Grid>
					<Grid item md={12} xs={12} style={{ padding: "20px 0" }}>
						<DialogActions style={{ padding: "0px 24px" }}>
							<Grid item md={3} xs={6} style={{ marginRight: "20px" }}>
								<Button
									fullWidth
									variant="contained"
									color="default"
									onClick={handleCancel}
								>
									Cancel
								</Button>
							</Grid>
							<Grid item md={3} xs={6} style={{ margin: 0 }}>
								<Button
									fullWidth
									startIcon={
										isSubmitLoading && <CircularProgress size="1rem" />
									}
									disabled={isSubmitLoading}
									variant="contained"
									color="secondary"
									onClick={closeSelectedWorkOrders}
								>
									Close orders
								</Button>
							</Grid>
						</DialogActions>
					</Grid>
				</Grid>
			</Dialog>
			<Snackbar
				open={closeSuccess}
				autoHideDuration={2500}
				onClose={handleClose}
			>
				<Alert severity="warning" onClose={handleClose}>
					Work order closed
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
